// src/pages/Warehouse/ProductTypes/AddProductTypeDrawer.jsx
import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Drawer,
  Form,
  Input,
  Space,
  Table,
  Switch,
  Typography,
  message,
  Divider,
  Tag,
  Tooltip,
} from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { db } from "../../../firebase/config"
import { addDoc, collection, doc, serverTimestamp, updateDoc } from "firebase/firestore"

const { Text } = Typography

// Fixed unit options
const UNIT_OPTIONS = [
  { label: "kg", value: "kg" },
  { label: "m²", value: "m2" },
  { label: "m", value: "m" },
  { label: "pcs", value: "pcs" },
  { label: "box", value: "box" },
]

// Base fields — always included
const BASE_FIELDS = [
  { key: "productName", label: "Product name", type: "text", required: true, locked: true },
  {
    key: "unit",
    label: "Unit",
    type: "select",
    required: true,
    locked: true,
    options: UNIT_OPTIONS,
  },
  { key: "amount", label: "Quantity", type: "number", required: true, locked: true },
  { key: "price", label: "Price", type: "number", required: true, locked: true },
  { key: "total", label: "Total", type: "number", required: true, locked: true },
]

// Optional fields — user can toggle on/off
const OPTIONAL_FIELDS_PRESET = [
  { key: "originalMeters", label: "Meters", type: "number", required: false, included: false },
  { key: "ean", label: "EAN", type: "text", required: false, included: false },
  { key: "barcode", label: "Barcode", type: "text", required: false, included: false },
  { key: "material", label: "Material", type: "text", required: false, included: false },
  { key: "materialWidth", label: "Width", type: "number", required: false, included: false },
  { key: "batchSeries", label: "Batch/Series", type: "text", required: false, included: false },
  {
    key: "manufacturerCode",
    label: "Manufacturer product code",
    type: "text",
    required: false,
    included: false,
  },
]

// helpers
const baseKeys = new Set(BASE_FIELDS.map((f) => f.key))

function normalizeOptionalStateFromDocFields(docFields = []) {
  // Split doc fields into base vs others
  const others = (docFields || []).filter((f) => !baseKeys.has(f.key))

  // Start with the preset list, mark included/required per doc
  const byKey = new Map(OPTIONAL_FIELDS_PRESET.map((f) => [f.key, { ...f }]))

  // Apply doc values onto the preset (or add unknown fields so we preserve them)
  others.forEach((f) => {
    if (byKey.has(f.key)) {
      const cur = byKey.get(f.key)
      byKey.set(f.key, {
        ...cur,
        included: true,
        required: !!f.required,
        // keep type/label from preset unless doc differs (prefer doc’s)
        type: f.type || cur.type,
        label: f.label || cur.label,
      })
    } else {
      // Unknown future field — keep it editable in this session
      byKey.set(f.key, {
        key: f.key,
        label: f.label || f.key,
        type: f.type || "text",
        required: !!f.required,
        included: true,
      })
    }
  })

  return Array.from(byKey.values())
}

export default function AddProductTypeDrawer({
  // Controlled mode (editing)
  open: controlledOpen,
  onClose: controlledOnClose,
  initialDoc = null, // { id, name, description, fields }
}) {
  const isControlled = typeof controlledOpen === "boolean"
  const [internalOpen, setInternalOpen] = useState(false)
  const open = isControlled ? controlledOpen : internalOpen
  const onClose = isControlled ? controlledOnClose : () => setInternalOpen(false)

  const [form] = Form.useForm()
  const [optionalFields, setOptionalFields] = useState(OPTIONAL_FIELDS_PRESET)

  const isEdit = !!initialDoc?.id

  // When editing, populate form + optional toggles
  useEffect(() => {
    if (!open) return
    if (isEdit) {
      form.setFieldsValue({
        name: initialDoc?.name || "",
        description: initialDoc?.description || "",
      })
      setOptionalFields(normalizeOptionalStateFromDocFields(initialDoc?.fields))
    } else {
      // fresh create
      form.resetFields()
      setOptionalFields(OPTIONAL_FIELDS_PRESET)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, initialDoc])

  const columns = useMemo(
    () => [
      {
        title: "Field",
        dataIndex: "label",
        key: "label",
        render: (label, record) => (
          <Space>
            <Text>{label}</Text>
            {record.key === "unit" ? <Tag>kg / m² / m / pcs / box</Tag> : null}
            {record.locked ? (
              <Tooltip title="Base fields are always included & required">
                <Tag>base</Tag>
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 160,
        render: (type) => <Text type="secondary">{type}</Text>,
      },
      {
        title: "Required",
        dataIndex: "required",
        key: "required",
        width: 120,
        render: (required, record) =>
          record.locked ? (
            <Switch checked disabled />
          ) : (
            <Switch
              checked={!!required}
              onChange={(checked) =>
                setOptionalFields((prev) =>
                  prev.map((f) => (f.key === record.key ? { ...f, required: checked } : f))
                )
              }
            />
          ),
      },
      {
        title: "Include",
        dataIndex: "included",
        key: "included",
        width: 120,
        render: (included, record) =>
          record.locked ? (
            <Switch checked disabled />
          ) : (
            <Switch
              checked={!!included}
              onChange={(checked) =>
                setOptionalFields((prev) =>
                  prev.map((f) => (f.key === record.key ? { ...f, included: checked } : f))
                )
              }
            />
          ),
      },
    ],
    []
  )

  const tableData = useMemo(() => [...BASE_FIELDS, ...optionalFields], [optionalFields])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const { name, description } = values

      const fields = [...BASE_FIELDS, ...optionalFields.filter((f) => f.included)].map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: !!f.required,
        ...(f.options ? { options: f.options } : {}),
      }))

      if (isEdit) {
        await updateDoc(doc(db, "warehouseProductTypes", initialDoc.id), {
          name: name.trim(),
          description: (description || "").trim(),
          fields,
          updatedAt: serverTimestamp(),
        })
        message.success("Product type updated!")
      } else {
        await addDoc(collection(db, "warehouseProductTypes"), {
          name: name.trim(),
          description: (description || "").trim(),
          fields,
          createdAt: serverTimestamp(),
        })
        message.success("Product type created!")
      }

      if (!isControlled) setInternalOpen(false)
      onClose?.()
    } catch (err) {
      if (err?.errorFields) return
      console.error(err)
      message.error("Failed to save the product type.")
    }
  }

  return (
    <>
      {/* Standalone trigger only if not controlled */}
      {!isControlled && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setInternalOpen(true)}>
          Add product type
        </Button>
      )}

      <Drawer
        title={isEdit ? "Edit product type" : "New product type"}
        open={open}
        width={720}
        onClose={onClose}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSave}>
              {isEdit ? "Save changes" : "Save"}
            </Button>
          </Space>
        }>
        <Form form={form} style={{ width: "100%" }} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g., Paper roll, Film, Adhesive…" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="(optional)" />
          </Form.Item>

          <Divider orientation="left">Fields</Divider>

          <Table
            size="small"
            columns={columns}
            dataSource={tableData}
            rowKey="key"
            pagination={false}
          />

          <div style={{ marginTop: 12 }}>
            <Text type="secondary">
              Base fields are always included. Toggle extra fields you need for this product type.
            </Text>
          </div>
        </Form>
      </Drawer>
    </>
  )
}
